/* ------------------------------------*\
    #TABLE STICKY SCRIPT
\*------------------------------------ */

import Component from './component';

/**
 * Add classes to tables when head row cells or first column cells are sticky.
 * @class TableSticky
 * @extends Component
 */

export default class TableSticky extends Component {

    static componentName = 'tables-sticky';

    static componentSelector = '.c-table--sticky-head, .c-table--sticky-column';

    static defaultOptions = {
        hasStickyHeadClass: 'has-sticky-head', // The class to add to the table wrapper when a head row cell is sticky
        hasStickyColumnClass: 'has-sticky-column', // The class to add to the table wrapper when a first column cell is sticky
        observerOptions: {
            threshold: [1], // The threshold for the IntersectionObserver (1 means the element is fully in view)
        },
    };

    /**
     * Detects when head row cells or first column cells become sticky and adds classes to the table wrapper.
     */
    tableSticky() {

        /**
         * Prepend `minimum-scale=1` to the `content` property of `<meta name="viewport">` if it not already exists.
         * This fixes a behavior in Chromium-based mobile browsers where `window.scrollX` is otherwise always `0`.
         * @see https://stackoverflow.com/a/59443204
         */
        function setViewportMinimumScale() {
            // Select the viewport meta tag from the document.
            const viewportMeta = document.querySelector('meta[name="viewport"]');

            // If the viewport meta tag exists
            if (viewportMeta) {
                const viewportContent = viewportMeta?.getAttribute('content');
                // If the content attribute does not include 'minimum-scale'
                if (!viewportContent?.includes('minimum-scale')) {
                    viewportMeta.setAttribute('content', `${viewportContent}, minimum-scale=1`);
                }
            } else {
                // If the viewport meta tag does not exist, create it.
                const meta = document.createElement('meta');
                meta.name = 'viewport';
                meta.content = 'width=device-width, initial-scale=1, minimum-scale=1';
                document.head.appendChild(meta);
            }
        }

        /**
         * Update the `scrollWidth` custom property and the `.has-overflow-clip` class on the document root.
         *
         * @param {HTMLElement} tableWrapper - The wrapper element of the table.
         * @param {HTMLElement} table - The table element.
         */
        function updateScrollWidth(tableWrapper, table) {
            if (!tableWrapper || !table) { return; }

            // Add `minimum-scale=1` to the `content` property of `<meta name="viewport">`
            // to fix scroll issue in Chromium-based mobile browsers.
            setViewportMinimumScale();

            const tableWrapperWidth = tableWrapper.getBoundingClientRect().width;
            const tableWidth = table.getBoundingClientRect().width;

            // The `.has-scroll-overflow` class is used to fake-scroll the table via CSS positioning,
            // but only when it’s necessary (when table is wider than its wrapper)
            tableWrapper.classList.toggle('has-scroll-overflow', tableWidth > tableWrapperWidth);

            // Remove `scrollWidth` custom property from document root to not affect the following assignment.
            document.documentElement.style.removeProperty('--scroll-width');
            // Remove `.has-overflow-clip` class from document root to not affect the following assignment.
            document.documentElement.classList.remove('has-overflow-clip');

            // Width of the scrollable area (including the over-scrolling tables).
            const { scrollWidth } = document.body;

            // Add `scrollWidth` as custom property to document root.
            // `--scroll-width` is used to set the width of the sticky body to avoid
            // too early or unnecessary horizontal page scrolling.
            document.documentElement.style.setProperty('--scroll-width', `${scrollWidth}px`);

            // Add `.has-overflow-clip` class to the document root.
            // Together with CSS this is a workaround for a bug in Firefox,
            // where it calculates an incorrect total scroll width.
            document.documentElement.classList.add('has-overflow-clip');
        }

        /**
         * Update transform values of root and tables based on the scroll status.
         * Negative `--scroll-offset` value will be used to position tables in the opposite scrolling direction.
         */
        function updateScrollOffset() {
            document.documentElement.style.setProperty('--scroll-offset', `${window.scrollX}px`);
        }

        try {
            const tableWrapper = this.element; // Get the table wrapper element
            const table = tableWrapper.querySelector('table');

            // Update `scrollWidth` on load.
            updateScrollWidth(tableWrapper, table);

            // Update `scrollWidth` when a sticky table changes dimensions.
            const resizeObserver = new ResizeObserver(() => { updateScrollWidth(tableWrapper, table); });

            // Observe table element.
            resizeObserver.observe(table);

            // Update `scrollWidth` on resize.
            let resizeRAF;
            window.addEventListener('resize', () => {
                cancelAnimationFrame(resizeRAF);
                resizeRAF = requestAnimationFrame(() => {
                    updateScrollWidth(tableWrapper, table);
                });
            }, { passive: true });

            // Update scroll offset on scroll.
            let scrollRAF;
            window.addEventListener('scroll', () => {
                cancelAnimationFrame(scrollRAF);
                scrollRAF = requestAnimationFrame(updateScrollOffset);
            }, { passive: true });

            // STICKY HEADER ROW

            // Cells in thead that could become sticky
            const headCells = Array.from(tableWrapper.querySelectorAll('thead tr > *'));

            // Observe head cells
            headCells.forEach((headCell) => {
                new IntersectionObserver(
                    (entries) => {
                        entries.forEach((cell) => {
                            // Top position of the table wrapper.
                            const tableWrapperTop = tableWrapper.getBoundingClientRect().top;

                            // Top position of the cell.
                            const cellTop = cell.boundingClientRect.top;

                            // Detect that the cell is sticky when its top boundary is above the viewport.
                            let isSticky = cellTop < 0;

                            // Detect that the cell is sticky when the table wrapper top boundary is above the (sticky) cell.
                            // This is the case when the table does not stick to the top edge of the viewport, but to another container.
                            if (tableWrapperTop - cellTop < 0) {
                                isSticky = tableWrapperTop - cellTop < 0;
                            }

                            // Add class to table wrapper when cell is sticky
                            tableWrapper.classList.toggle(
                                this.options.hasStickyHeadClass,
                                isSticky,
                            );
                        });
                    },
                    this.options.observerOptions,
                ).observe(headCell);
            });

            // STICKY FIRST COLUMN

            // Cells in the first column that could become sticky
            const firstColumnCells = Array.from(tableWrapper.querySelectorAll('tr > :first-child'));

            // Observe first column cells
            firstColumnCells.forEach((firstColumnCell) => {
                new IntersectionObserver(
                    (entries) => {
                        entries.forEach((cell) => {
                            // Left position of the table wrapper.
                            const tableWrapperLeft = tableWrapper.getBoundingClientRect().left;

                            // Left position of the cell.
                            const cellLeftPos = cell.boundingClientRect.left;

                            // If the table wrapper or `table` have a left margin, we need to calculate with it.
                            const tableWrapperMarginLeft = parseFloat(window.getComputedStyle(tableWrapper).marginLeft);
                            const tableMarginLeft = parseFloat(window.getComputedStyle(tableWrapper.querySelector('table')).marginLeft);
                            const marginLeft = tableWrapperMarginLeft + tableMarginLeft;

                            // Detect that the cell is sticky when its left boundary is less than 0px from the left border of the viewport.
                            let isSticky = cellLeftPos < 0;

                            // Detect that the cell is sticky when the table wrapper (+ table margin) is further left than the cell.
                            // This is the case when the table does not stick to the left edge of the viewport, but to another container.
                            isSticky = tableWrapperLeft + marginLeft - cellLeftPos !== 0 ? true : isSticky;

                            // Add class to table wrapper when cell is sticky
                            tableWrapper.classList.toggle(
                                this.options.hasStickyColumnClass,
                                isSticky,
                            );
                        });
                    },
                    this.options.observerOptions,
                ).observe(firstColumnCell);
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(`tableSticky(): ${e}`);
        }
    }

    constructor() {
        super(...arguments);
        this.tableSticky();
    }

}

TableSticky.register();
